import { mapActions, mapGetters } from 'vuex';
import { smartbtoa } from '@/utils/helpers';
import loaderMixin from '@/mixins/loader';
import alertBox from '@/components/generics/alert-box/alert-box.vue';

const disConfig = {
  api_key: process.env.DISQUS_PUBLIC_KEY,
  sso: {
    name: process.env.DISQUS_SSO_NAME,
    button: process.env.DISQUS_SSO_BUTTON,
    icon: process.env.DISQUS_SSO_ICON,
    url: process.env.DISQUS_SSO_URL,
    logout: process.env.DISQUS_SSO_LOGOUT,
    width: process.env.DISQUS_SSO_WIDTH,
    height: process.env.DISQUS_SSO_HEIGHT,
  },
};

function disqusConfig() {
  this.page.identifier = disConfig.identifier;
  this.page.url = disConfig.url;
  this.page.title = disConfig.title;
  this.page.api_key = disConfig.api_key;
  this.sso = disConfig.sso;

  this.page.remote_auth_s3 = disConfig.remote_auth_s3 ? disConfig.remote_auth_s3 : '';
}

function buildDisqusUrl(url, commentId = '') {
  const baseUrl = (url || `${window.location.href}`).split('#')[0];

  if (!commentId) {
    return baseUrl;
  }

  return `${baseUrl}#comment-${commentId}`;
}

function normalizeCommentThreadId(commentThreadId) {
  const normalized = Number(commentThreadId);

  return Number.isFinite(normalized) && normalized > 0 ? normalized : 0;
}

export default {
  mixins: [
    loaderMixin,
  ],
  props: {
    showTitle: {
      type: Boolean,
      default: true,
    },
    disqusId: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    auth: String,
    url: String,
    marketId: {
      default: null,
    },
    previous: {
      default: null,
    },
    next: {
      default: null,
    },
    total: {
      default: null,
    },
    isActive: {
      type: Boolean,
      default: false,
    },
    focusCommentId: {
      type: String,
      default: '',
    },
    requestedCommentThreadId: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      currentThread: 0,
      currentTitle: this.title,
      previousThread: this.previous,
      nextThread: this.next,
      totalComments: this.total,
      currentDisqusId: this.disqusId,
      currentDisqusUrl: this.url,
      currentDisqusTitle: this.title,
      appliedFocusCommentId: this.focusCommentId,
      isReconcilingRequest: false,
      showExplanation: false,
    };
  },
  components: {
    alertBox,
  },
  computed: {
    ...mapGetters('app', ['isLayoutMobile', 'isCommentEnabled']),
    ...mapGetters('user', ['isLoggedIn']),
  },
  mounted() {
    if (this.auth && this.isLoggedIn) {
      this.enableDisqus(this.auth, this.focusCommentId);
    } else {
      this.enableDisqus(null, this.focusCommentId);
    }

    this.reconcileRequestedState();
  },
  methods: {
    ...mapActions('market', ['getCommentThread']),
    applyThreadData(data, threadId) {
      this.currentThread = normalizeCommentThreadId(threadId);
      this.currentTitle = data.disqus_ThreadName;
      this.nextThread = data.disqus_NextThreadID;
      this.previousThread = data.disqus_PrevThreadID;
      this.totalComments = data.disqus_ArchivedCommentsTotal;
      this.currentDisqusId = data.disqus_Identifier;
      this.currentDisqusUrl = data.disqus_Url;
      this.currentDisqusTitle = data.disqus_ThreadName;
    },
    emitDiscussionRequestChange(commentThreadId = 0, commentId = '') {
      this.$emit('discussion-request-change', {
        commentId,
        commentThreadId: normalizeCommentThreadId(commentThreadId),
      });
    },
    reload(payload, { syncState = true } = {}) {
      const threadId = normalizeCommentThreadId(payload.threadId);
      const commentId = payload.commentId || '';

      this.isReconcilingRequest = true;

      if (syncState) {
        this.emitDiscussionRequestChange(threadId, commentId);
      }

      return this.getCommentThread({
        ...payload,
        threadId,
      })
        .then((data) => {
          this.applyThreadData(data, threadId);

          if (this.auth && this.isLoggedIn) {
            this.enableDisqus(this.auth, commentId);
          } else {
            this.enableDisqus(null, commentId);
          }
        })
        .finally(() => {
          this.isReconcilingRequest = false;
          this.$nextTick(() => {
            this.reconcileRequestedState();
          });
        });
    },
    reconcileRequestedState() {
      if (!this.isActive || this.isReconcilingRequest) {
        return;
      }

      const requestedThreadId = normalizeCommentThreadId(this.requestedCommentThreadId);
      const requestedCommentId = this.focusCommentId || '';

      if (requestedThreadId !== this.currentThread) {
        // Reconcile same-market thread changes inside Disqus so the rest of the page stays mounted.
        this.reload({
          threadId: requestedThreadId,
          marketId: this.marketId,
          commentId: requestedCommentId,
        }, {
          syncState: false,
        });
        return;
      }

      if (requestedCommentId === this.appliedFocusCommentId) {
        return;
      }

      this.isReconcilingRequest = true;

      if (this.auth && this.isLoggedIn) {
        this.enableDisqus(this.auth, requestedCommentId);
      } else {
        this.enableDisqus(null, requestedCommentId);
      }

      this.isReconcilingRequest = false;
    },
    onCurrent(commentThreadId) {
      this.reload({
        threadId: commentThreadId,
        marketId: this.marketId,
      });
    },
    onPrevious() {
      this.reload({
        threadId: this.previousThread,
        marketId: this.marketId,
      });
    },
    onNext() {
      this.reload({
        threadId: this.nextThread,
        marketId: this.marketId,
      });
    },
    enableDisqus(auth, commentId = '') {
      const shortname = process.env.DISQUS_SHORTNAME;
      const resolvedAuth = this.$router.resolve({
        name: 'disqus-auth',
      });
      const resolvedLogout = this.$router.resolve({
        name: 'disqus-logout',
        params: {
          routeName: smartbtoa(this.$route.name),
          routeParams: smartbtoa(JSON.stringify(this.$route.params)),
        },
      });

      disConfig.identifier = this.currentDisqusId || this.disqusId;
      disConfig.url = buildDisqusUrl(this.currentDisqusUrl || this.url, commentId);
      disConfig.title = this.currentDisqusTitle || this.title;
      disConfig.sso.width = this.isLayoutMobile ? 320 : 800;
      disConfig.sso.url = `${window.location.origin}${resolvedAuth.href}`;
      disConfig.sso.logout = `${window.location.origin}${resolvedLogout.href}`;
      // Note: SSO payloads expire after 2 hours. - now storing in ls
      disConfig.remote_auth_s3 = auth || '';
      this.appliedFocusCommentId = commentId;

      if (window.DISQUS) {
        window.DISQUS.reset({
          reload: true,
          config: disqusConfig,
        });
      } else {
        window.disqus_config = disqusConfig;
        const existingScript = document.querySelector('[data-disqus-embed="true"]');

        if (!existingScript) {
          const script = document.createElement('script');
          script.type = 'text/javascript';
          script.src = `//${shortname}.disqus.com/embed.js`;
          script.setAttribute('data-disqus-embed', 'true');
          document.body.appendChild(script);
        }
      }
    },
  },
  watch: {
    isActive() {
      this.reconcileRequestedState();
    },
    focusCommentId() {
      this.reconcileRequestedState();
    },
    requestedCommentThreadId() {
      this.reconcileRequestedState();
    },
  },
};



// WEBPACK FOOTER //
// ./src/components/disqus/disqus.js